import { Routes, Route, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFCM } from "../utils/useFCM";

import { Menu } from "lucide-react";
import { motion } from "framer-motion";

import Sidebar from "../components/Sidebar";
import Home from "./home";
import PostEvent from "./post-event";
import ManageEvents from "./manges-events/manage-event";
import ManageEventsDetail from "./manges-events/manage-event-detail";
import EditEvent from "./manges-events/edit-event";
import Header from "../components/reuseable/header";
import Collaborate from "./explore-nonProfits/collaborate";
import WorkSpace from "./collaborate-workspace/workspack";
import ExploreNonProfit from "./explore-nonProfits/explore-nonprofit";
import NewRequest from "./collaborate-workspace/new-request";
import TrackVoluntier from "./collaborate-workspace/track-volunteir";
import Impact from "./impact-dashboard/impact-page";
import VolunteerMatching from "./volunteer-matching/volunteer-matching-page";
import UserSetting from "./user-setting/user-setting";
import Notification from "./notification";
import PersonalSpacePage from "./personal-space/index";
import AdminInquiriesPage from "./admin-inquiries/index";
import AdminInquiryDetailPage from "./admin-inquiries/admin-inquiry-detail-page";
import ManageResources from "./manage-resources/manage-resources";
import ManageResourceDetail from "./manage-resources/manage-resource-detail";
import ManageResourceFormPage from "./manage-resources/manage-resource-form";
import AssistantsPage from "./assistants/index";
import AssistantFormPage from "./assistants/assistant-form";
import { FetchMyAdminInquiries } from "../Store/Actions/AdminInquiryActions";
import AnnouncementPopup from "../components/AnnouncementPopup";
import { BASE_URL, API_ENDPOINTS } from "../config/api.config";

function Dashboard() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapseBar, setIsCollapseBar] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginSuccess = useSelector((state) => state.auth?.loginSuccess);

  // Initialize Firebase Cloud Messaging for push notifications
  useFCM();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getCollpase = (value) => {
    setIsCollapseBar(value);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsMobileMenuOpen(false);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Authentication check - redirect to login if not authenticated
  useEffect(() => {
    const checkAuth = () => {
      // Check both Redux state and localStorage as fallback
      const hasReduxAuth = loginSuccess && loginSuccess.data;
      const hasLocalStorageAuth = localStorage.getItem("loginSuccess") && localStorage.getItem("accessToken");
      
      if (!hasReduxAuth && !hasLocalStorageAuth) {
        // Clear any stale data
        localStorage.removeItem("loginSuccess");
        localStorage.removeItem("accessToken");
        // Redirect to login
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [loginSuccess, navigate]);

  // Pre-fetch inquiry count for sidebar badge
  useEffect(() => {
    const hasAuth = (loginSuccess && loginSuccess.data) || (localStorage.getItem("accessToken"));
    if (hasAuth) {
      dispatch(FetchMyAdminInquiries());
    }
  }, [dispatch, loginSuccess]);

  // Show announcement popup once per login (cleared on logout via sessionStorage)
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    if (sessionStorage.getItem("announcementShown")) return;
    sessionStorage.setItem("announcementShown", "true");

    // Check user preference before showing popup
    fetch(`${BASE_URL}${API_ENDPOINTS.NONPROFIT_GET_PROFILE}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((profileResult) => {
        const prefs = profileResult?.data?.preferences;
        // If announcements preference is explicitly set to false, skip
        if (prefs && prefs.announcements === false) return;

        return fetch(`${BASE_URL}${API_ENDPOINTS.ACTIVE_ANNOUNCEMENT}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((result) => {
            if (result?.data) setActiveAnnouncement(result.data);
          });
      })
      .catch(() => {/* silently ignore */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex lg:flex-row flex-col w-full">
      {activeAnnouncement && (
        <AnnouncementPopup
          announcement={activeAnnouncement}
          onClose={() => setActiveAnnouncement(null)}
        />
      )}
      <div className={`${isCollapseBar ? "lg:w-[5.5%]" : "lg:w-[24%]"}`}>
        <button
          className={`fixed top-3 left-4 z-50 ${
            isMobileMenuOpen ? "hidden" : ""
          } lg:hidden bg-gray-300 p-2 rounded-full`}
          onClick={toggleMobileMenu}
        >
          <Menu className="h-6 w-6" />
        </button>
        <Sidebar
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          isCollapse={getCollpase}
        />
      </div>
      <motion.div
        initial={{ x: 0 }}
        animate={isLargeScreen && isCollapseBar ? { x: 0 } : {}}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        className={`${
          isCollapseBar ? " w-full" : "lg:w-[76%] w-full"
        }  overflow-y-auto}`}
      >
        <Header clases={"px-7"} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/post-event" element={<PostEvent />} />
          <Route path="/manage-events" element={<ManageEvents />} />
          <Route path="/manage-event-detail/:id" element={<ManageEventsDetail />} />
          <Route path="/manage-events/:id/edit" element={<EditEvent />} />
          <Route path="/explore-nonprofit" element={<ExploreNonProfit />} />
          <Route path="/collaborate/:id" element={<Collaborate />} />
          <Route path="/collaboration-workspace" element={<WorkSpace />} />
          <Route path="/new-request/:requestId?" element={<NewRequest />} />
          <Route path="/track-volunteer" element={<TrackVoluntier />} />
          <Route path="/impact-dashboard" element={<Impact />} />
          <Route path="/volunteer-matching" element={<VolunteerMatching />} />
          <Route path="/user-setting" element={<UserSetting />} />
          <Route path="/notification" element={<Notification />} />
          <Route path="/personal-space" element={<PersonalSpacePage />} />
          <Route path="/admin-inquiries" element={<AdminInquiriesPage />} />
          <Route path="/admin-inquiries/:id" element={<AdminInquiryDetailPage />} />
          <Route path="/manage-resources" element={<ManageResources />} />
          <Route path="/manage-resources/new" element={<ManageResourceFormPage />} />
          <Route path="/manage-resources/:id" element={<ManageResourceDetail />} />
          <Route path="/manage-resources/:id/edit" element={<ManageResourceFormPage />} />
          <Route path="/assistants" element={<AssistantsPage />} />
          <Route path="/assistants/add" element={<AssistantFormPage />} />
          <Route path="/assistants/:id/edit" element={<AssistantFormPage />} />
        </Routes>
      </motion.div>
    </div>
  );
}

export default Dashboard;
